import React, { FC, PropsWithChildren, useEffect } from "react";
import { Theme } from "@telia/teddy";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import {
    APPEARANCE_COOKIE_MAX_AGE_DAYS,
    DARK_THEME_CLASS_NAME,
    SYSTEM_APPEARANCE_COOKIE_NAME,
    resolveAppearance,
} from "../../../../../shared/appearance";
import { RootState } from "../../../../../models";
import { setSystemAppearance } from "../../state/configActions";
import { getAppearancePreference, getSystemAppearance, isRetailer as isRetailerSelector } from "../../state/selectors";

interface AppThemeProps {
    forceLight?: boolean;
}

const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

const AppTheme: FC<PropsWithChildren<AppThemeProps>> = ({ forceLight = false, children }) => {
    const dispatch = useDispatch();
    const isRetailer = useSelector((state: RootState) => isRetailerSelector(state));
    const preference = useSelector((state: RootState) => getAppearancePreference(state));
    const systemAppearance = useSelector((state: RootState) => getSystemAppearance(state));
    const effectiveAppearance = resolveAppearance({ isRetailer, appearance: preference, systemAppearance, forceLight });
    const followsSystem = isRetailer && !forceLight && preference === "system";

    // Lets <html> and <body> follow the theme too (the server renders the same class on <html>)
    useEffect(() => {
        document.documentElement.classList.toggle(DARK_THEME_CLASS_NAME, effectiveAppearance === "dark");
    }, [effectiveAppearance]);

    // Follows the OS setting live, and keeps the cookie the server uses for the next request up to date
    useEffect(() => {
        if (!followsSystem || typeof window.matchMedia !== "function") {
            return undefined;
        }
        const query = window.matchMedia(DARK_SCHEME_QUERY);
        const sync = () => {
            const value = query.matches ? "dark" : "light";
            Cookies.set(SYSTEM_APPEARANCE_COOKIE_NAME, value, {
                expires: APPEARANCE_COOKIE_MAX_AGE_DAYS,
                path: "/",
                sameSite: "lax",
            });
            dispatch(setSystemAppearance(value));
        };
        sync();
        query.addEventListener("change", sync);
        return () => query.removeEventListener("change", sync);
    }, [followsSystem, dispatch]);

    return (
        <Theme appearance={effectiveAppearance} theme="telia" className="rapidshop-theme">
            {children}
        </Theme>
    );
};

export default AppTheme;
